import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import BlogManager from './BlogManager'
import CaseStudyManager from './CaseStudyManager'
import ContactMessages from './ContactMessages'
import AdminSettings from './AdminSettings'
import MediaManager from './MediaManager'
import AuthorManager from './AuthorManager'
import DeploymentDashboard from './DeploymentDashboard'
import { logoutAdmin } from '../utils/auth'

const {
  FiHome, FiEdit, FiMail, FiSettings, FiLogOut, FiFileText, FiFolderOpen, 
  FiActivity, FiPlus, FiBell, FiMenu, FiX, FiEye, FiImage, FiChevronDown, 
  FiUser, FiUsers, FiRocket
} = FiIcons

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showNewContentMenu, setShowNewContentMenu] = useState(false)
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalCaseStudies: 0,
    totalAuthors: 0,
    totalMessages: 0,
    totalViews: 0,
    recentActivity: []
  })

  useEffect(() => {
    const loadStats = async () => {
      try {
        const posts = JSON.parse(localStorage.getItem('blog_posts') || '[]')
        const caseStudies = JSON.parse(localStorage.getItem('case_studies') || '[]')
        const authors = JSON.parse(localStorage.getItem('authors') || '[]')
        const messages = JSON.parse(localStorage.getItem('contact_messages') || '[]')
        const views = parseInt(localStorage.getItem('total_views') || '0')

        setStats({
          totalPosts: posts.length,
          totalCaseStudies: caseStudies.length,
          totalAuthors: authors.length,
          totalMessages: messages.length,
          totalViews: views,
          recentActivity: [
            ...messages.slice(-3).map(m => ({
              type: 'message',
              title: `New message from ${m.fullName || m.full_name || 'Unknown'}`,
              time: m.timestamp || new Date().toISOString()
            })),
            ...posts.slice(-3).map(p => ({
              type: 'post',
              title: `Blog post: ${p.title}`,
              time: p.createdAt || p.created_at || new Date().toISOString()
            })),
            ...caseStudies.slice(-2).map(cs => ({
              type: 'case_study',
              title: `Case study: ${cs.title}`,
              time: cs.createdAt || cs.created_at || new Date().toISOString()
            })),
            ...authors.slice(-2).map(a => ({
              type: 'author',
              title: `New author: ${a.name}`,
              time: a.createdAt || a.created_at || new Date().toISOString()
            }))
          ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 6)
        })
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    }

    loadStats()
    const interval = setInterval(loadStats, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleLogout = () => {
    logoutAdmin()
    onLogout()
  }

  // Handle new content creation
  const handleNewContent = (type) => {
    setShowNewContentMenu(false)
    switch (type) {
      case 'blog':
        setActiveTab('blog')
        setTimeout(() => {
          const event = new CustomEvent('createNewBlogPost')
          window.dispatchEvent(event)
        }, 100)
        break
      case 'case-study':
        setActiveTab('case-studies')
        setTimeout(() => {
          const event = new CustomEvent('createNewCaseStudy')
          window.dispatchEvent(event)
        }, 100)
        break
      case 'author':
        setActiveTab('authors')
        setTimeout(() => {
          const event = new CustomEvent('createNewAuthor')
          window.dispatchEvent(event)
        }, 100)
        break
      case 'media':
        setActiveTab('media')
        break
      default:
        break
    }
  }

  const menuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: FiHome },
    { id: 'deployment', name: 'Deploy', icon: FiRocket },
    { id: 'blog', name: 'Blog', icon: FiEdit },
    { id: 'case-studies', name: 'Portfolio', icon: FiFolderOpen },
    { id: 'authors', name: 'Authors', icon: FiUsers },
    { id: 'media', name: 'Media', icon: FiImage },
    { id: 'messages', name: 'Messages', icon: FiMail },
    { id: 'settings', name: 'Settings', icon: FiSettings }
  ]

  const newContentOptions = [
    { type: 'blog', label: 'New Blog Post', icon: FiEdit, description: 'Create a new blog article' },
    { type: 'case-study', label: 'New Case Study', icon: FiFolderOpen, description: 'Add a new portfolio project' },
    { type: 'author', label: 'New Author', icon: FiUsers, description: 'Add a new author profile' },
    { type: 'media', label: 'Upload Media', icon: FiImage, description: 'Add images or files' }
  ]

  const renderContent = () => {
    switch (activeTab) {
      case 'blog': return <BlogManager />
      case 'case-studies': return <CaseStudyManager />
      case 'authors': return <AuthorManager />
      case 'media': return <MediaManager />
      case 'messages': return <ContactMessages />
      case 'settings': return <AdminSettings />
      case 'deployment': return <DeploymentDashboard />
      default:
        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600 text-sm">Welcome back! Here's your content overview.</p>
              </div>
              
              {/* Quick Deploy Button */}
              <div className="flex items-center space-x-3">
                <motion.button
                  onClick={() => setActiveTab('deployment')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all duration-200 shadow-lg"
                >
                  <SafeIcon icon={FiRocket} className="h-4 w-4" />
                  <span>Deploy Site</span>
                </motion.button>

                {/* New Content Dropdown */}
                <div className="relative">
                  <motion.button
                    onClick={() => setShowNewContentMenu(!showNewContentMenu)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 shadow-lg"
                  >
                    <SafeIcon icon={FiPlus} className="h-4 w-4" />
                    <span>New Content</span>
                    <SafeIcon icon={FiChevronDown} className={`h-4 w-4 transition-transform ${showNewContentMenu ? 'rotate-180' : ''}`} />
                  </motion.button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {showNewContentMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-50"
                      >
                        <div className="p-2">
                          {newContentOptions.map((option, index) => (
                            <motion.button
                              key={option.type}
                              onClick={() => handleNewContent(option.type)}
                              whileHover={{ backgroundColor: '#f3f4f6' }}
                              className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-start space-x-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <SafeIcon icon={option.icon} className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-medium text-gray-900">{option.label}</h3>
                                  <p className="text-sm text-gray-500">{option.description}</p>
                                </div>
                              </div>
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: 'Blog Posts', value: stats.totalPosts, icon: FiFileText, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Case Studies', value: stats.totalCaseStudies, icon: FiFolderOpen, color: 'text-green-600', bg: 'bg-green-50' },
                { label: 'Authors', value: stats.totalAuthors, icon: FiUsers, color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: 'Messages', value: stats.totalMessages, icon: FiMail, color: 'text-orange-600', bg: 'bg-orange-50' },
                { label: 'Views', value: stats.totalViews.toLocaleString(), icon: FiEye, color: 'text-pink-600', bg: 'bg-pink-50' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm font-medium">{stat.label}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bg} rounded-lg flex items-center justify-center`}>
                      <SafeIcon icon={stat.icon} className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                  <SafeIcon icon={FiActivity} className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {stats.recentActivity.length > 0 ? (
                  stats.recentActivity.map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          activity.type === 'message' ? 'bg-purple-100 text-purple-600' :
                          activity.type === 'post' ? 'bg-blue-100 text-blue-600' :
                          activity.type === 'author' ? 'bg-green-100 text-green-600' :
                          'bg-orange-100 text-orange-600'
                        }`}>
                          <SafeIcon icon={
                            activity.type === 'message' ? FiMail :
                            activity.type === 'post' ? FiFileText :
                            activity.type === 'author' ? FiUsers : FiFolderOpen
                          } className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(activity.time).toLocaleDateString()} at {new Date(activity.time).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="px-6 py-12 text-center">
                    <SafeIcon icon={FiActivity} className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No recent activity</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'New Blog Post', icon: FiEdit, action: () => handleNewContent('blog'), color: 'bg-blue-600 hover:bg-blue-700' },
                { label: 'Add Case Study', icon: FiFolderOpen, action: () => handleNewContent('case-study'), color: 'bg-green-600 hover:bg-green-700' },
                { label: 'New Author', icon: FiUsers, action: () => handleNewContent('author'), color: 'bg-purple-600 hover:bg-purple-700' },
                { label: 'View Messages', icon: FiMail, action: () => setActiveTab('messages'), color: 'bg-orange-600 hover:bg-orange-700' }
              ].map((action, index) => (
                <motion.button
                  key={index}
                  onClick={action.action}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`${action.color} text-white p-4 rounded-lg transition-all duration-200 text-left`}
                >
                  <SafeIcon icon={action.icon} className="h-5 w-5 mb-2" />
                  <p className="font-medium text-sm">{action.label}</p>
                </motion.button>
              ))}
            </div>
          </div>
        )
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNewContentMenu && !event.target.closest('.relative')) {
        setShowNewContentMenu(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showNewContentMenu])

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <motion.div
        className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-gray-200 transition-all duration-300 relative ${
          mobileMenuOpen ? 'fixed inset-y-0 left-0 z-50' : 'hidden'
        } lg:block`}
        animate={{ width: sidebarCollapsed ? 64 : 256 }}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h1 className="font-bold text-gray-900">aiximo</h1>
                  <p className="text-xs text-gray-500">Admin</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-2">
          {menuItems.map((item) => (
            <motion.button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id)
                setMobileMenuOpen(false)
              }}
              whileHover={{ x: 2 }}
              className={`w-full flex items-center space-x-3 px-3 py-2 mb-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={item.icon} className="h-4 w-4 flex-shrink-0" />
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="truncate"
                  >
                    {item.name}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-4 left-2 right-2">
          <motion.button
            onClick={handleLogout}
            whileHover={{ x: 2 }}
            className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 text-sm font-medium"
          >
            <SafeIcon icon={FiLogOut} className="h-4 w-4 flex-shrink-0" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <motion.button
              onClick={() => setMobileMenuOpen(true)}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiMenu} className="h-5 w-5" />
            </motion.button>
            <h1 className="font-bold text-gray-900">aiximo Admin</h1>
            <motion.button
              onClick={handleLogout}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="h-5 w-5" />
            </motion.button>
          </div>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard